import { API } from "@swivl-mono/api-helper";
import { Channel, EscalationInterface, OnlineRosterEntry, SessionInterface, UserInterface } from "@swivl-mono/types";
import { create } from "zustand";

import LocationsModel from "../Locations/Locations.Model";
import { Member } from "../Member/Member.Current";
import { UsersModeEscalationMethods } from "./Methods/Users.Model.Escalation.Methods";
import { UsersModelSessionMethods } from "./Methods/Users.Model.Session.Methods";
import { UsersModelSetDataMethod } from "./Methods/Users.Model.SetData.Methods";
import { UsersModelDebounceManager } from "./Methods/Users.Model.SetDataDebounce.Manager";
import { UsersModelUsersMethods } from "./Methods/Users.Model.Users.Methods";

export interface UserConversationsInterface {
  name: string;
  lastUserMessage?: string;
  lastUserMessageAt?: Date;
  lastUserMessageChannel?: Channel;
  user?: UserInterface;
  escalations?: { [escalationsId: number]: EscalationInterface };
  sessions?: { [channel in Channel]?: SessionInterface };
}

export interface UsersModelStoreInterface {
  moreUsersToLoad: boolean;
  loadedUsersPage: number;
  isLoadingMoreUsers: boolean;
  users?: { [id: number]: UserInterface };
  escalations?: { [escalationsId: number]: EscalationInterface };
  unacknowledgedEscalations: EscalationInterface[];

  sessions?: { [sessionId: number]: SessionInterface };
  conversations?: { [userId: number]: UserConversationsInterface };
  usersOnline?: { [userId: number]: OnlineRosterEntry };
}

export const UsersModelBlankState: UsersModelStoreInterface = {
  users: undefined,
  moreUsersToLoad: true,
  loadedUsersPage: 0,
  isLoadingMoreUsers: false,
  escalations: undefined,
  unacknowledgedEscalations: [],
  sessions: undefined,
  conversations: undefined
};

export class UsersModel {
  static useState = create<UsersModelStoreInterface>((set) => ({ ...UsersModelBlankState }));
  static internalSetState = UsersModel.useState.setState;
  static get state() {
    return UsersModel.useState.getState();
  }
  static shouldRefreshActive = false;

  static reset() {
    UsersModel.internalSetState({ ...UsersModelBlankState });
    UsersModel.shouldRefreshActive = false;
  }

  static async loadAll() {
    LocationsModel.getGroupsAndLocations(false);
    const [sessions, escalations] = await Promise.all([
      API.Sessions.getActiveSessions(Member.orgId),
      await API.Escalations.getAllActiveEscalations(Member.orgId)
    ]);

    // Passing an empty array of users. We're not going to load all users
    UsersModel.setUserSessionOrEscalation([], sessions, escalations);
  }
  static async loadForUser(userId: number) {
    console.log("PUF z loadForUser - loading for userId:", userId);
    const [user, sessions, escalations] = await Promise.all([
      API.Users.getUserById(userId),
      API.Sessions.getActiveSessionsForUser(Member.orgId, userId),
      API.Escalations.getEscalationsForUser(Member.orgId, userId)
    ]);
    if (!user) {
      console.warn("PUF z loadForUser - no user found for id:", userId);
      return;
    }
    console.log("PUF z loadForUser handler:", { user, sessions, escalations });
    UsersModel.setUserSessionOrEscalation([user], sessions, escalations);
  }

  // Setting User, Session and Escalation Data
  static setUserSessionOrEscalation = UsersModelDebounceManager.createUsersModelThrottledSetter(UsersModelSetDataMethod);

  // User Methods
  static loadUsersByIds = UsersModelUsersMethods.loadUsersByIds;
  static getUserById = UsersModelUsersMethods.getUserById;
  static updateUser = UsersModelUsersMethods.updateUser;
  static searchForUser = UsersModelUsersMethods.searchForUser;
  static loadUsers = UsersModelUsersMethods.loadUsers;
  static loadNextPageOfArchivedUsers = UsersModelUsersMethods.loadNextPageOfArchivedUsers;
  static getUsersByPhoneNumber = UsersModelUsersMethods.getUsersByPhoneNumber;
  static updateSingleUser = UsersModelUsersMethods.updateSingleUser;
  static setArchivedForUser = UsersModelUsersMethods.setArchivedForUser;

  // Session Methods
  // static loadAllSessions = UsersModelSessionMethods.loadAllSessions;
  static sessionUpdated = UsersModelSessionMethods.sessionUpdated;
  static takeoverConversationOnChannel = UsersModelSessionMethods.takeoverConversationOnChannel;
  static endTakeoverOfSession = UsersModelSessionMethods.endTakeoverOfSession;
  static getSession = UsersModelSessionMethods.getSession;

  // Escalation Methods
  // static loadAllEscalations = UsersModeEscalationMethods.loadAllEscalations;
  static loadUnacknowledgedEscalations = UsersModeEscalationMethods.loadUnacknowledgedEscalations;
  static escalationUpdated = UsersModeEscalationMethods.escalationUpdated;
  static ignoreEscalation = UsersModeEscalationMethods.ignoreEscalation;
  static acceptEscalation = UsersModeEscalationMethods.acceptEscalation;
  static acknowledgeEscalation = UsersModeEscalationMethods.acknowledgeEscalation;
  static acknowledgeEscalationById = UsersModeEscalationMethods.acknowledgeEscalationById;
}
